import { Inject, Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { compact, concat, filter, find, forEach, includes, map, omitBy, sortBy } from 'lodash';
import { Observable, Subject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { TValidSchoolTypes } from '../../../shared/typings/interfaces/school.interface';
import {
  BulkUpdateMilestones,
  BulkUpdateStudents,
  getMilestoneLoadedEntities,
  LoadStudentMilestones,
} from '../../../store';
import { IRowDataWithBatchActions } from '../../components/list/fixed-table/fixed-table.component';
import { IStudentEditableField, studentEditableFieldMap } from '../../constants/paths/student-editable-map.constant';
import { IBatchEditorProjection, IEditableFields } from '../../modals/batch-edit/batch-edit-modal.component';
import { IRowData, TList } from '../../models/list-models';
import { IUser, IUserMiniWithRole } from '../../typings/interfaces/user.interface';
import { ApiService } from '../api-service/api-service';
import { ImSchool } from '../im-models/im-school';
import { ImUser } from '../im-models/im-user';
import { IMilestoneEditable, MilestonesKeyMap } from './../../constants/milestones.constant';

export interface IBatchEditStudentParams {
  studentIds: string[];
  schoolId: string;
  patch: {
    path: any;
    newValue: any;
    dataType: any;
    key: any;
  };
  destroy$: Subject<any>;
}

export interface IEditableRecord {
  humanName: string;
  editable: boolean;
  dataType: string;
  dataTypeOptions?: {
    canBeNull?: boolean;
    values?: any[];
    canBeRemoved?: boolean;
  };
  category?: string;
  schoolLevels?: Array<'ES' | 'MS' | 'HS'>;
}

export interface IExistingBatchActionData {
  mappedData: IRowDataWithBatchActions[][];
  count: number;
}

@Injectable()
export class BatchEditService implements OnDestroy {
  constructor(
    @Inject('SorterColumn') public sorterColumn: any,
    private imUser: ImUser,
    private imSchool: ImSchool,
    private apiService: ApiService,
    private store: Store<any>,
  ) {}

  editableFields: any;
  items;
  pointPersonFields;

  ngOnDestroy() {
    // console.log('BatchEditService is destroyed');
  }

  getStudentsPointPeople(params: IBatchEditorProjection): Observable<any> {
    return this.apiService.getStudents(params);
  }

  getAuthorizedPointPeople(users: IUserMiniWithRole[]) {
    const authorizedUsers = map(users, (user: IUser) => {
      const isViewUser = this.imUser.isViewType(user);
      const isClusterUser = this.imUser.isClusterUser(user);
      const isNoAccessUser = this.imUser.isNoAccessUser(user);
      if (!isViewUser && !isClusterUser && !isNoAccessUser) return user;
    });
    return sortBy(compact(authorizedUsers), user => {
      return user.name.firstName;
    });
  }

  getAuthorizedAdvisors(users: IUserMiniWithRole[]) {
    // view type users can be advisors / guidance counselors (JE)
    const authorizedUsers = map(users, (user: IUserMiniWithRole) => {
      const isClusterUser = this.imUser.isClusterUser(user);
      const isNoAccessUser = this.imUser.isNoAccessUser(user);
      if (!isClusterUser && !isNoAccessUser) return user;
    });
    return sortBy(compact(authorizedUsers), user => {
      return user.name.firstName;
    });
  }

  getEditableFields(currentUser: Partial<IUser>, schoolType?: TValidSchoolTypes): IEditableFields[] {
    // load permitted fields from sorter col service
    this.sorterColumn.setEditablePermissions(currentUser);
    const sorterCols = this.sorterColumn.getSorterColumnNamesAndCategories({ editable: true });
    const sorterColsSubset = this.restrictSorterColumns(sorterCols);
    let editableFields = map(studentEditableFieldMap, (studentEditableField: IStudentEditableField) => {
      const { humanName, category, schoolLevels } = studentEditableField;
      return { humanName, category, schoolLevels };
    });
    const sorterAndEditableFieldsCols = concat(sorterColsSubset, editableFields);
    const colsBySchoolType = this.getColumnsBySchoolType(sorterAndEditableFieldsCols, schoolType);
    // load simple milestone batch edit options
    const simpleMilestones = omitBy(MilestonesKeyMap, (props, milestone) => {
      return props.isCalculated;
    });
    const hsTypes = ['6 to 12', 'Small HS', 'Large HS', 'Transfer'];
    const includeMilestones = includes(hsTypes, schoolType);
    // milestones are only options for schools with high school students (JE)
    const milestoneFields = includeMilestones
      ? map(simpleMilestones, (milestoneField: IMilestoneEditable) => {
          const { humanName, category } = milestoneField;
          return { humanName, category };
        })
      : [];

    const allCols = concat(colsBySchoolType, milestoneFields);
    return allCols;
  }

  getColumnsBySchoolType(columns: IEditableFields[], schoolType?: TValidSchoolTypes): IEditableFields[] {
    if (schoolType) {
      const schoolLevel = this.imSchool.getSchoolLevel(schoolType);
      columns = filter(columns, (col: IEditableFields) => {
        return schoolLevel.some(level => col.schoolLevels.includes(level));
      });
    }
    return columns;
  }

  getBooleanDropdown() {
    return ['Yes', 'No'];
  }

  getUsersDropdown(users) {
    const usersDropdown = map(users, (user: IUserMiniWithRole) => {
      const { _id, gafeEmail, doeEmail } = user;
      // Preferred name is now used if available; otherwise, defaults to name
      const firstName = user.preferredName.firstName || user.name.firstName;
      const lastName = user.preferredName.lastName || user.name.lastName;
      return {
        saveValue: {
          userId: _id,
          firstName,
          lastName,
          gafeEmail,
          doeEmail,
        },
        displayValue: {
          name: `${firstName} ${lastName}`,
          email: this.imUser.getEffectiveEmail({ gafeEmail, doeEmail } as IUser),
        },
      };
    });

    // add null option as first value in dropdown
    usersDropdown.unshift({
      saveValue: null,
      displayValue: {
        name: '-',
        email: '',
      },
    });

    return usersDropdown;
  }

  sortUsersByLastName(users: IUserMiniWithRole[]) {
    return sortBy(users, (user: IUserMiniWithRole) => {
      return user.name.lastName;
    });
  }

  // FORM FIELDS - PROCESSING USER INPUT

  getField(newField: string): IStudentEditableField {
    if (!newField) return;
    let field;
    const v3Field = find(studentEditableFieldMap, field => {
      return field.humanName === newField;
    });
    const simpleMilestone = find(MilestonesKeyMap, (milestone: IMilestoneEditable) => {
      return milestone.humanName === newField && !milestone.isCalculated;
    });

    if (v3Field) field = v3Field;
    else if (simpleMilestone) field = simpleMilestone;
    else field = this.sorterColumn.getByHumanName(newField);

    return field;
  }

  setBooleanPatch(field: string) {
    switch (field) {
      case 'Yes':
        return true;
      case 'No':
        return false;
      case '-':
        return null;
      case null:
        return null;
      default:
        console.warn('Invalid menu choice');
    }
  }

  filterInput(val: string, editableFieldOptions: IEditableFields[]) {
    return editableFieldOptions.filter(option => {
      let valLowerCase = val.toLowerCase();
      return (
        option.humanName.toLowerCase().includes(valLowerCase) || option.category.toLowerCase().includes(valLowerCase)
      );
    });
  }

  restrictSorterColumns(sorterCols: IEditableFields[]): IEditableFields[] {
    const colsToRemove = ['Applying to CUNY', 'Expected to Meet CUNY Benchmarks', 'Post Secondary Plans'];
    return filter(sorterCols, (col: IEditableFields) => {
      return !includes(colsToRemove, col.humanName);
    });
  }

  // accessed by v2 grid toolbar and v3 batch actions
  batchEditStudents(params: IBatchEditStudentParams): void {
    const { studentIds, schoolId, patch } = params; // add type for use by calling comps as well
    const { path, newValue, dataType, key } = patch;
    if (dataType === 'MILESTONE') {
      // check if milestones are loaded for these studentIds, and if not, load them (JE)
      // needed because background jobs only return the patch, not the full document

      // BatchEditService has been attached as a provider to the components that call this,
      // so when those components are destroyed, this service is as  well.
      // As such, no need to manage subscription (JYR)
      this.store
        .select(getMilestoneLoadedEntities)
        .pipe(
          tap(loadedEntities => {
            forEach(studentIds, studentId => {
              if (!loadedEntities[studentId]) {
                const payload = { schoolId, studentId };
                this.store.dispatch(new LoadStudentMilestones(payload));
              }
            });
          }),
        )
        .subscribe();
      // for this modal, milestoneIds are not known, but studentIds are
      // the where query is used by the endpoint to decide which milestones to patch (JE)
      const payload = {
        milestoneIds: [],
        status: newValue,
        where: {
          studentIds,
          key,
          schoolId,
        },
      };
      this.store.dispatch(new BulkUpdateMilestones(payload));
    } else {
      const payload = {
        patches: {
          _ids: studentIds,
          path,
          newValue,
        },
      };
      this.store.dispatch(new BulkUpdateStudents(payload));
    }
  }

  getSelectedRowsHash(batchEditRowsIds: string[]): { [key: string]: boolean } {
    const rowIdHash = batchEditRowsIds.reduce((hash, rowId) => {
      hash[rowId] = true;
      return hash;
    }, {});
    return rowIdHash;
  }

  getRowDataWithBatchActions(
    idHash: { [key: string]: boolean },
    rowData: IRowData[][],
    listType: TList,
  ): IExistingBatchActionData {
    let matched = 0;
    let batchActionRows = {
      mappedData: rowData.map(
        (row: any): IRowDataWithBatchActions[] => {
          const stub = row[0];
          const metaData = JSON.parse(stub.meta);
          let id = metaData.data;
          // 1. using student._id in new student lists but batch actions requires osis id
          // 2. using user._id in user lists and batch actions uses the same id
          // 3. using shelterId in shelter user lists and batch actions uses the same id.
          const portfolioUserList = listType === 'USER_SCHOOL_PORTFOLIO' || listType === 'USER_SHELTER_PORTFOLIO';
          id = portfolioUserList ? id : id.slice(0, -6);
          const matchedId = idHash[id];
          row.isChecked = matchedId ? true : false;
          if (row.isChecked) matched++;
          return row;
        },
      ),
      count: matched,
    };
    return batchActionRows;
  }
}
